/**
 * Decorative shapes — pure SVG/CSS ornaments. Accessible-hidden.
 */

const palette = {
  candy: "#8B5CF6",
  pink: "#F472B6",
  yellow: "#FBBF24",
  mint: "#34D399",
  ink: "#1E293B",
  white: "#FFFFFF",
};

export const Blob = ({ tone = "candy", size = 320, className = "", style = {} }) => (
  <div
    aria-hidden
    style={{ width: size, height: size, background: palette[tone], ...style }}
    className={`rounded-blob border-2 border-ink ${className}`}
  />
);

export const ConfettiDecoration = ({ className = "", density = "normal" }) => {
  const shapes = density === "dense" ? 16 : density === "sparse" ? 6 : 10;
  const items = Array.from({ length: shapes }).map((_, i) => {
    const tones = ["candy", "pink", "yellow", "mint"];
    const tone = tones[i % tones.length];
    const shape = ["rect", "circle", "tri", "cross"][i % 4];
    const top = Math.floor((i * 37) % 100);
    const left = Math.floor((i * 59 + 11) % 100);
    const rotate = (i * 47) % 360;
    const size = 10 + ((i * 7) % 20);
    const dur = 4 + ((i * 3) % 6);
    return (
      <span
        key={i}
        aria-hidden
        style={{
          top: `${top}%`,
          left: `${left}%`,
          transform: `rotate(${rotate}deg)`,
          animation: `float ${dur}s ease-in-out ${(i * 0.2).toFixed(1)}s infinite`,
        }}
        className="absolute"
      >
        {shape === "rect" ? (
          <span
            style={{ width: size, height: size, background: palette[tone] }}
            className="block rounded-md border-2 border-ink"
          />
        ) : shape === "circle" ? (
          <span
            style={{ width: size, height: size, background: palette[tone] }}
            className="block rounded-full border-2 border-ink"
          />
        ) : shape === "tri" ? (
          <svg width={size + 6} height={size + 6} viewBox="0 0 28 28">
            <polygon
              points="14,2 26,26 2,26"
              fill={palette[tone]}
              stroke={palette.ink}
              strokeWidth="3"
              strokeLinejoin="round"
            />
          </svg>
        ) : (
          <svg width={size + 6} height={size + 6} viewBox="0 0 28 28">
            <path
              d="M4 14 L24 14 M14 4 L14 24"
              stroke={palette[tone]}
              strokeWidth="6"
              strokeLinecap="round"
            />
          </svg>
        )}
      </span>
    );
  });
  return (
    <div aria-hidden className={`pointer-events-none absolute inset-0 ${className}`}>
      {items}
    </div>
  );
};

export const SectionPattern = ({ kind = "dots", className = "", opacity = 1 }) => {
  const cls =
    kind === "grid"
      ? "tex-grid-playful"
      : kind === "diag"
        ? "tex-diag"
        : kind === "soft"
          ? "tex-dots-soft"
          : "tex-dots";
  return (
    <div
      aria-hidden
      style={{ opacity }}
      className={`pointer-events-none absolute inset-0 ${cls} ${className}`}
    />
  );
};

export const SquigglyLine = ({ className = "", color = "ink" }) => (
  <svg aria-hidden viewBox="0 0 200 20" className={className} preserveAspectRatio="none">
    <path
      d="M0 10 Q 20 0, 40 10 T 80 10 T 120 10 T 160 10 T 200 10"
      fill="none"
      stroke={palette[color]}
      strokeWidth="3"
      strokeLinecap="round"
    />
  </svg>
);

export const StarBurst = ({ tone = "yellow", size = 56, className = "" }) => (
  <svg aria-hidden viewBox="0 0 100 100" width={size} height={size} className={className}>
    <polygon
      points="50,4 58,38 94,42 64,64 76,96 50,76 24,96 36,64 6,42 42,38"
      fill={palette[tone]}
      stroke={palette.ink}
      strokeWidth="4"
      strokeLinejoin="round"
    />
  </svg>
);

export default ConfettiDecoration;
